import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, map, mapTo, tap } from 'rxjs/operators'

import { ApiService } from './api.service'
import { Theme } from '@shared/models/theme.model'
import { FALLBACK_THEME, FALLBACK_THEMES_LIST } from 'src/environments/go-api-fallbacks'

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private currentTheme = new BehaviorSubject<Theme>(null)
  currentTheme$ = this.currentTheme.asObservable()

  constructor(private apiService: ApiService) {}

  applyTheme() {
    return this.getTheme().pipe(
      tap((theme) => theme && this.setTheme(theme)),
      mapTo(() => null),
    )
  }

  getThemesList() {
    return this.apiService.get('/themes').pipe(
      map((resp) => resp?.['data']),
      catchError(() => of(FALLBACK_THEMES_LIST)),
    )
  }

  private getTheme(): Observable<Theme> {
    return this.apiService.get('/themes/my').pipe(
      map((response) => (response?.['data'] || response) as Theme),
      catchError(() => of(FALLBACK_THEME as unknown as Theme)),
    )
  }

  discardTheme() {
    if (!this.currentTheme.value) {
      return
    }

    const style = document.documentElement.style
    const { id, name, ...themeStyles } = this.currentTheme.value

    this.currentTheme.next(null)
    this.mapPropertyToVariableName(themeStyles).forEach(([variable]) => style.setProperty(variable, null))
  }

  private setTheme(theme: Theme) {
    this.currentTheme.next(theme)
    this.setStyleVariables(theme)
  }

  private setStyleVariables(theme: Theme) {
    const style = document.documentElement.style
    const { id, name, ...themeStyles } = theme

    this.mapPropertyToVariableName(themeStyles).forEach(([variable, value]) => style.setProperty(variable, value))
  }

  private mapPropertyToVariableName(properties: object) {
    return Object.keys(properties).map((camelCaseProperty) => [this.toCssVariable(camelCaseProperty), properties[camelCaseProperty]])
  }

  private toCssVariable(str: string) {
    return (
      '--' +
      str
        .split(/([A-Z][a-z]*)/)
        .filter(Boolean)
        .map((word) => word.toLowerCase())
        .join('-')
    )
  }
}
